import { TextBlock } from "hec/components/text-block";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  ContentButtonNavigation,
} from "hec/components/ui/carousel";
import type { CSSProperties } from "react";
import { useId } from "react";

import { HeroBackgroundImage } from "./background-image";
import type { ISingleColumnProps } from "./types";
import { getResponsiveHeroBgStyles } from "./utils";

const NO_BG_STYLES = { style: {}, mobileCss: "" };

const SingleColumn = (props: ISingleColumnProps) => {
  const baseId = useId();
  const {
    backgroundType,
    backgroundImage,
    backgroundImageMobile,
    backgroundColor,
    backgroundGradient,
    contentPosition,
    contentPositionMobile,
    external,
    external_link,
    link,
    couponCode,
    enableCouponCode,
    eyebrowText,
    eyebrowIcon,
    eyebrowType,
    disclaimerText,
    headlineType,
    headlineImage,
    headlineText,
    textBlock,
    callToAction,
    buttonColor,
    buttonTextColor,
    enableOverlay,
    overlayColor,
    textAlignment,
    eyebrowBackgroundColor,
    eyebrowSize,
    eyebrowTextColor,
    carouselItems = [],
    carouselDots,
    attributes,
    priority = false,
  } = props;

  const heroId = `hero-sc-${baseId.replace(/:/g, "")}`;
  // COM-181: image backgrounds render as a real absolutely-positioned <img>
  // (see HeroBackgroundImage) — CSS background-image is LCP-invisible by
  // spec. Color/gradient backgrounds keep the style path.
  const isImageBg = backgroundType === "image";
  const { style: bgStyle, mobileCss } = isImageBg
    ? NO_BG_STYLES
    : getResponsiveHeroBgStyles({
        id: heroId,
        backgroundType,
        backgroundImage,
        backgroundImageMobile,
        backgroundColor,
        backgroundGradient,
      });

  let alignItemsMd = "center";
  if (contentPosition === "left") alignItemsMd = "flex-start";
  if (contentPosition === "right") alignItemsMd = "flex-end";

  let justifyContentMobile = "center";
  if (contentPositionMobile === "top") justifyContentMobile = "flex-start";
  if (contentPositionMobile === "bottom") justifyContentMobile = "flex-end";

  const flexStyle: CSSProperties = {
    alignItems: "center",
    justifyContent: justifyContentMobile,
    ["--align-items-md" as string]: alignItemsMd,
  };

  const resolvedLink = external
    ? (external_link?.value?.data?.url ?? "")
    : (link?.value?.data?.url ?? "");

  return backgroundType === "carousel" ? (
    <Carousel
      opts={{
        align: "start",
        loop: true,
      }}
      className={`w-full ${(attributes as Record<string, string>)?.className || ""}`}
    >
      <CarouselContent>
        {carouselItems.map((item, index) => {
          const carouselHeroId = `hero-sc-c-${baseId.replace(/:/g, "")}-${index}`;
          const itemIsImageBg =
            (item.carouselBackgroundType || "image") === "image";
          const { style: carouselBgStyle, mobileCss: carouselMobileCss } =
            itemIsImageBg
              ? NO_BG_STYLES
              : getResponsiveHeroBgStyles({
                  id: carouselHeroId,
                  backgroundType: item.carouselBackgroundType || "image",
                  backgroundImage: item.backgroundImage,
                  backgroundImageMobile: item.backgroundImageMobile,
                  backgroundColor: item.backgroundColor,
                  backgroundGradient: item.backgroundGradient,
                });

          let carouselAlignItemsMd = "center";
          if (item.contentPosition === "left")
            carouselAlignItemsMd = "flex-start";
          if (item.contentPosition === "right")
            carouselAlignItemsMd = "flex-end";

          let carouselJustifyContentMobile = "center";
          if (item.contentPositionMobile === "top")
            carouselJustifyContentMobile = "flex-start";
          if (item.contentPositionMobile === "bottom")
            carouselJustifyContentMobile = "flex-end";

          const carouselFlexStyle: CSSProperties = {
            alignItems: "center",
            justifyContent: carouselJustifyContentMobile,
            ["--align-items-md" as string]: carouselAlignItemsMd,
          };

          const carouselResolvedLink = item.external
            ? (item.external_link?.value?.data?.url ?? "")
            : (item.link?.value?.data?.url ?? "");

          return (
            <CarouselItem key={index}>
              {carouselMobileCss && <style>{carouselMobileCss}</style>}
              <section
                id={carouselHeroId}
                className="relative w-full"
                style={carouselBgStyle}
              >
                {itemIsImageBg && (
                  <HeroBackgroundImage
                    backgroundImage={item.backgroundImage}
                    backgroundImageMobile={item.backgroundImageMobile}
                    // Only the first slide is on screen at load (COM-163
                    // convention) — the rest must not compete for bandwidth.
                    priority={priority && index === 0}
                  />
                )}
                <div className="relative mx-auto px-4">
                  <div
                    className="flex min-h-[400px] flex-col"
                    style={carouselFlexStyle}
                  >
                    <style>{`
                      @media (min-width: 992px) {
                        [style*='--align-items-md'] {
                          align-items: var(--align-items-md) !important;
                          justify-content: center !important;
                        }
                      }
                    `}</style>
                    <div className="flex w-full max-w-[356px] items-center justify-center lg:max-w-[650px]">
                      <TextBlock
                        couponCode={item.couponCode}
                        enableCouponCode={item.enableCouponCode}
                        eyebrow={item.eyebrowText}
                        eyebrowIcon={item.eyebrowIcon}
                        eyebrowType={item.eyebrowType}
                        disclaimer={item.disclaimerText ?? ""}
                        headlineType={item.headlineType}
                        headlineImage={item.headlineImage}
                        headline={item.headlineText ?? ""}
                        supportText={item.textBlock ?? ""}
                        cta={item.callToAction ?? ""}
                        buttonColor={item.buttonColor}
                        buttonTextColor={item.buttonTextColor}
                        external={item.external}
                        link={carouselResolvedLink}
                        overlay={item.enableOverlay}
                        overlayColor={item.overlayColor}
                        alignment={item.textAlignment}
                        eyebrowBackgroundColor={item.eyebrowBackgroundColor}
                        eyebrowSize={item.eyebrowSize}
                        eyebrowTextColor={item.eyebrowTextColor}
                      />
                    </div>
                  </div>
                </div>
              </section>
            </CarouselItem>
          );
        })}
      </CarouselContent>
      {carouselItems.length > 1 && carouselDots !== false && (
        <ContentButtonNavigation
          buttonType="circleButton"
          data-testid="homepage-hero-carousel"
        />
      )}
    </Carousel>
  ) : (
    <>
      {mobileCss && <style>{mobileCss}</style>}
      <section
        id={heroId}
        className={`relative w-full ${(attributes as Record<string, string>)?.className || ""}`}
        style={bgStyle}
      >
        {isImageBg && (
          <HeroBackgroundImage
            backgroundImage={backgroundImage}
            backgroundImageMobile={backgroundImageMobile}
            priority={priority}
          />
        )}
        <div className="relative mx-auto mb-5 px-4">
          <div className="flex min-h-[400px] flex-col" style={flexStyle}>
            <style>{`
              @media (min-width: 992px) {
                [style*='--align-items-md'] {
                  align-items: var(--align-items-md) !important;
                  justify-content: center !important;
                }
              }
            `}</style>
            <div className="flex w-full max-w-[356px] items-center justify-center lg:max-w-[650px]">
              <TextBlock
                couponCode={couponCode}
                enableCouponCode={enableCouponCode}
                eyebrow={eyebrowText}
                eyebrowIcon={eyebrowIcon}
                eyebrowType={eyebrowType}
                disclaimer={disclaimerText ?? ""}
                headlineType={headlineType}
                headlineImage={headlineImage}
                headline={headlineText ?? ""}
                supportText={textBlock ?? ""}
                cta={callToAction ?? ""}
                buttonColor={buttonColor}
                buttonTextColor={buttonTextColor}
                external={external}
                link={resolvedLink}
                overlay={enableOverlay}
                overlayColor={overlayColor}
                alignment={textAlignment}
                eyebrowBackgroundColor={eyebrowBackgroundColor}
                eyebrowSize={eyebrowSize}
                eyebrowTextColor={eyebrowTextColor}
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SingleColumn;
